import { X, User, Users, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { Avatar } from './Avatar';

export function NewConversationModal({
  isOpen,
  isDark,
  conversationType,
  groupName,
  allUsers,
  selectedUsers,
  onClose,
  onTypeChange,
  onGroupNameChange,
  onSelectUser,
  onRemoveUser,
  onCreate
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={cn(
        'w-full max-w-md rounded-2xl border overflow-hidden animate-slide-up',
        isDark ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
      )}>
        <div className={cn(
          'flex items-center justify-between p-4 border-b',
          isDark ? 'border-white/10' : 'border-gray-200'
        )}>
          <h2 className={cn('text-lg font-bold', isDark ? 'text-white' : 'text-gray-900')}>
            Nouvelle conversation
          </h2>
          <button
            onClick={onClose}
            className={cn(
              'p-2 rounded-lg transition-colors',
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
            )}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Type selector */}
          <div className="flex gap-2">
            <button
              onClick={() => onTypeChange('direct')}
              className={cn(
                'flex-1 py-2 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors',
                conversationType === 'direct'
                  ? 'bg-red-500 text-white'
                  : isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              <User size={18} />
              Direct
            </button>
            <button
              onClick={() => onTypeChange('group')}
              className={cn(
                'flex-1 py-2 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors',
                conversationType === 'group'
                  ? 'bg-red-500 text-white'
                  : isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              <Users size={18} />
              Groupe
            </button>
          </div>
          
          {/* Group name */}
          {conversationType === 'group' && (
            <Input
              type="text"
              value={groupName}
              onChange={(e) => onGroupNameChange(e.target.value)}
              placeholder="Nom du groupe"
              className={isDark ? 'bg-zinc-800 border-white/10 text-white' : ''}
            />
          )}
          
          {/* User selection label */}
          <p className={cn(
            'text-sm font-medium',
            isDark ? 'text-zinc-400' : 'text-gray-600'
          )}>
            {conversationType === 'direct' ? 'Sélectionner un destinataire' : 'Sélectionner les participants'}
          </p>
          
          {/* Users list */}
          <div className={cn(
            'rounded-xl border max-h-64 overflow-y-auto',
            isDark ? 'bg-zinc-800/50 border-white/10' : 'bg-gray-50 border-gray-200'
          )}>
            {allUsers.length === 0 ? (
              <div className="p-4 text-center">
                <p className={isDark ? 'text-zinc-500' : 'text-gray-500'}>
                  Aucun utilisateur disponible
                </p>
              </div>
            ) : (
              allUsers.map((u) => {
                const isSelected = selectedUsers.some(su => su.id === u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() => isSelected ? onRemoveUser(u.id) : onSelectUser(u)}
                    className={cn(
                      'w-full flex items-center gap-3 p-3 text-left transition-colors border-b last:border-b-0',
                      isDark ? 'border-white/5' : 'border-gray-100',
                      isSelected 
                        ? 'bg-red-500/10 border-l-4 border-l-red-500' 
                        : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                    )}
                  >
                    <Avatar user={u} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        'font-medium truncate',
                        isDark ? 'text-white' : 'text-gray-900'
                      )}>
                        {u.prenom} {u.nom}
                      </p>
                      <p className={cn(
                        'text-xs truncate',
                        isDark ? 'text-zinc-500' : 'text-gray-500'
                      )}>
                        {u.role} • {u.email}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
          
          {/* Selected users summary */}
          {selectedUsers.length > 0 && (
            <div className={cn(
              'p-3 rounded-xl',
              isDark ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-100'
            )}>
              <p className={cn(
                'text-sm font-medium mb-2',
                isDark ? 'text-red-400' : 'text-red-600'
              )}>
                {selectedUsers.length} {selectedUsers.length === 1 ? 'personne sélectionnée' : 'personnes sélectionnées'}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((u) => (
                  <span
                    key={u.id}
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs',
                      isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-700 shadow-sm'
                    )}
                  >
                    {u.prenom} {u.nom}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveUser(u.id);
                      }}
                      className="hover:text-red-500 ml-1"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Create button */}
          <Button
            onClick={onCreate}
            disabled={selectedUsers.length === 0 || (conversationType === 'group' && !groupName.trim())}
            className="w-full bg-red-500 hover:bg-red-600 text-white"
          >
            {conversationType === 'group' ? 'Créer le groupe' : 'Démarrer la conversation'}
          </Button>
        </div>
      </div>
    </div>
  );
}
